/**
 * PortalInvitationNote - the wizard review line for the Brokermandat's portal invitation.
 *
 * The plan is API-computed (the render/prepare response's `invitation`); this only words it.
 */

import { PORTAL_INVITATION_HINT_TEXT } from '@brokly/shared';
import type { PortalInvitationPlanDto } from '@brokly/shared';

/** The review sentence for a plan; null when there is nothing to say. */
export function portalInvitationNoteText(plan: PortalInvitationPlanDto): string | null {
  const grants =
    plan.grantInvites > 0
      ? ` Weitere Unterzeichnende erhalten eine eigene Einladung: ${plan.grantInvites}.`
      : '';
  switch (plan.planned) {
    case 'invite':
    case 'renew':
      return `Portal-Einladung: wird mitgesendet (gültig ${plan.linkDays} Tage), vor der Grussformel eingefügt.${grants}`;
    case 'active':
      return `Kundenportal bereits aktiv.${grants}`;
    case 'existing_account':
      return `Kundenportal: Der Kunde hat bereits einen Zugang und meldet sich damit an.${grants}`;
    case 'none':
      if (plan.hint === 'portal_not_enabled') return 'Kundenportal nicht aktiviert.';
      return plan.hint !== null ? `Portal-Einladung nicht möglich: ${PORTAL_INVITATION_HINT_TEXT[plan.hint]}.` : null;
  }
}

export function PortalInvitationNote({ plan }: { plan: PortalInvitationPlanDto | null | undefined }) {
  if (!plan) return null;
  const text = portalInvitationNoteText(plan);
  if (text === null) return null;
  return (
    <div className="flex flex-col gap-1" data-testid="portal-invitation">
      <p className="rounded border bg-muted/40 px-3 py-2 text-xs" data-testid="portal-invitation-note">
        {text}
      </p>
      {plan.previewText ? (
        // Read only: the block is not part of the editable text; the send inserts it.
        <pre
          className="whitespace-pre-wrap rounded border border-dashed bg-background px-3 py-2 font-sans text-xs text-muted-foreground"
          aria-label="Portal-Einladung (wird beim Versand eingefügt)"
          data-testid="portal-invitation-preview"
        >
          {plan.previewText}
        </pre>
      ) : null}
    </div>
  );
}
